/**
 * deviceFactory.js -- Config builders that convert UI form values into
 * the sysconfig shape for each device type, plus the target resolver
 * that maps device_type/storage_mode to collection + builder.
 *
 * This module is intentionally pure: it does not generate IDs, perform
 * network calls, or depend on browser state. ID assignment happens in
 * the mutation layer.
 *
 * @typedef {import("../types/deviceTypes").DeviceFormValues} DeviceFormValues
 */

import { num, pctToFrac } from "./units";

function withOptionalDeviceId(config, { deviceId } = {}) {
  if (!deviceId) {
    return config;
  }
  return {
    ...config,
    device_id: deviceId,
  };
}

// ---------------------------------------------------------------------------
// Per-type config builders (form values -> sysconfig shape)
// ---------------------------------------------------------------------------

function toStorageBaseConfig(fv, options) {
  return withOptionalDeviceId({
    name: fv.device_name || "Unknown",
    make_and_model: fv.make_and_model || "Unknown Model",
    serial_number: (fv.serial_number || "").trim() || "",
    maximum_capacity_kwh: num(fv.max_energy) || 0,
    soc_limit_fractions: {
      min_soc_fraction: pctToFrac(fv.min_soc) || 0,
      max_soc_fraction: pctToFrac(fv.max_soc) || 0,
    },
    ave_parasitic_load_kw: num(fv.avg_parasitic_load) || 0,
    fractional_self_discharge: num(fv.fractional_self_loss) || 0,
    daily_cycles: num(fv.daily_cycles) || 0,
    initial_soc_fraction: pctToFrac(fv.initial_soc) || 0,
    efficiency_fractions: {
      charging_efficiency_fraction:
        pctToFrac(fv.charging_efficiency_fraction) || 0,
      discharging_efficiency_fraction:
        pctToFrac(fv.discharging_efficiency_fraction) || 0,
    },
    power_limits: {
      maximum_charging_power_kw: num(fv.max_power_charging) || 0,
      maximum_discharging_power_kw: num(fv.max_power_discharging) || 0,
    },
    charging_strategy: fv.charging_strategy || "unrestricted",
    discharging_strategy: fv.discharging_strategy || "unrestricted",
    coupled_generator_device_ids: [],
    status: fv.status || "connected",
  }, options);
}

export function toEvConfig(fv, options) {
  const base = toStorageBaseConfig(fv, options);
  base.availability_schedule_csv = fv.csv_file_name || "";
  base.ave_away_hourly_discharge_fraction =
    pctToFrac(fv.average_energy_used_during_operation) || 0;
  if (fv.start_date) base.start_date = fv.start_date;
  if (fv.end_date) base.end_date = fv.end_date;
  return base;
}

export function toBatteryConfig(fv, options) {
  return toStorageBaseConfig(fv, options);
}

export function toSolarConfig(fv, options) {
  const yearlyDegradation =
    fv.yearly_degradation === "" || fv.yearly_degradation == null
      ? 0.01
      : num(fv.yearly_degradation);

  const config = withOptionalDeviceId({
    name: fv.device_name || "Unknown",
    make_and_model: fv.make_and_model || "Unknown Model",
    serial_number: (fv.serial_number || "").trim() || "",
    max_operating_power_kw: num(fv.max_power_charging) || 0,
    min_operating_power_kw: num(fv.min_power_charging) || 0,
    startup_shutdown_cost: num(fv.startup_cost) || 0,
    degradation_yearly_kw_fraction: yearlyDegradation,
    historical_production_csv: fv.csv_file_name || null,
    coupled_bidirectional_device_ids: [],
    status: fv.status || "connected",
  }, options);
  if (fv.start_date) config.start_date = fv.start_date;
  if (fv.end_date) config.end_date = fv.end_date;
  return config;
}

export function toGeneratorConfig(fv, options) {
  return withOptionalDeviceId({
    name: fv.device_name || "Unknown",
    make_and_model: fv.make_and_model || "Unknown Model",
    serial_number: (fv.serial_number || "").trim() || "",
    max_operating_power_kw: num(fv.max_power_charging) || 0,
    min_operating_power_kw: num(fv.min_power_charging) || 0,
    startup_shutdown_cost: num(fv.startup_cost) || 0,
    fuel_type: fv.fuel_type || "carbon",
    fuel_storage_size_kg: num(fv.fuel_storage_size) || 0,
    consumption_kg_per_kwh: num(fv.consumption) || 0,
    carbon_emissions_g_per_kg: num(fv.carbon_emissions) || 0,
    fuel_cost_per_kg: num(fv.fuel_cost_per_kg) || 0,
    fuel_cost_escalator_fraction: num(fv.fuel_cost_escalator_fraction) || 0,
    coupled_bidirectional_device_ids: [],
    status: fv.status || "connected",
  }, options);
}

export function toConsumptionConfig(fv, options) {
  const config = withOptionalDeviceId({
    name: fv.device_name || "Unknown",
    make_and_model: fv.make_and_model || "Unknown Model",
    serial_number: (fv.serial_number || "").trim() || "",
    max_operating_power_kw: num(fv.max_power_charging) || 0,
    historical_load_csv: fv.csv_file_name || "",
    status: fv.status || "connected",
  }, options);
  if (fv.start_date) config.start_date = fv.start_date;
  if (fv.end_date) config.end_date = fv.end_date;
  return config;
}

export function toTransferConfig(fv) {
  const numVal = num;
  const demandCharge =
    fv.demand_charge && typeof fv.demand_charge === "object"
      ? {
          type: fv.demand_charge.type || "noncoincident",
          rate: numVal(fv.demand_charge.rate) || 0,
          escalator_fraction: numVal(fv.demand_charge.escalator_fraction) || 0,
          ...(fv.demand_charge.type === "ratcheted" && {
            ratchet_percentage:
              numVal(fv.demand_charge.ratchet_percentage) || 0.8,
            lookback_months:
              parseInt(fv.demand_charge.lookback_months, 10) || 12,
            historical_peak_kw:
              numVal(fv.demand_charge.historical_peak_kw) || 0,
          }),
        }
      : undefined;

  return {
    name: fv.device_name || "Unknown",
    make_and_model: fv.make_and_model || "Unknown Model",
    serial_number: (fv.serial_number || "").trim() || "",
    utility_tariff_csv: fv.csv_file_name || "",
    building_load: { historical_load_csv: "" },
    demand_charge: demandCharge,
    status: fv.status || "connected",
    ...(fv.start_date && { start_date: fv.start_date }),
    ...(fv.end_date && { end_date: fv.end_date }),
  };
}

// ---------------------------------------------------------------------------
// Target resolver: maps form values to collection + config builder
// ---------------------------------------------------------------------------

/**
 * @param {DeviceFormValues} formValues
 * @returns {{ collectionName: string|null, buildConfig: Function, isMeterLevel?: boolean }|null}
 */
export function resolveDeviceTarget(formValues) {
  if (!formValues.device_type) {
    return null;
  }
  if (formValues.device_type === "Storage") {
    if (formValues.storage_mode === "EV") {
      return { collectionName: "evs", buildConfig: toEvConfig };
    }
    return { collectionName: "batteries", buildConfig: toBatteryConfig };
  }
  if (formValues.device_type === "Production") {
    if (formValues.production_type === "Fuel generator") {
      return {
        collectionName: "fueled_generators",
        buildConfig: toGeneratorConfig,
      };
    }
    return { collectionName: "solar_arrays", buildConfig: toSolarConfig };
  }
  if (formValues.device_type === "Consumption") {
    return {
      collectionName: "uncontrollable_loads",
      buildConfig: toConsumptionConfig,
    };
  }
  if (formValues.device_type === "Transfer") {
    return {
      collectionName: null,
      buildConfig: toTransferConfig,
      isMeterLevel: true,
    };
  }
  return null;
}
